import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api';

type PickerKind = 'vehicle' | 'driver' | 'client' | 'site' | 'project';

interface PickerProps {
  kind: PickerKind;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Optional filter, e.g. only active vehicles. Defaults to 'active'. */
  status?: 'active' | 'all';
}

interface Row { id: string; label: string }

const CACHE = new Map<string, Row[]>();

async function fetchList(kind: PickerKind, status: 'active' | 'all'): Promise<Row[]> {
  const cacheKey = `${kind}:${status}`;
  if (CACHE.has(cacheKey)) return CACHE.get(cacheKey)!;
  let path: string;
  switch (kind) {
    case 'vehicle': path = `/api/v1/vehicles?pageSize=500${status === 'active' ? '&status=active' : ''}`; break;
    case 'driver':  path = `/api/v1/drivers?pageSize=500${status === 'active' ? '&status=active' : ''}`; break;
    case 'client':  path = `/api/v1/clients?pageSize=500`; break;
    case 'site':    path = `/api/v1/sites?pageSize=500`; break;
    case 'project': path = `/api/v1/projects?pageSize=500`; break;
  }
  try {
    const r = await apiClient.get<{ items?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>>(path);
    const data = Array.isArray(r.data) ? r.data : (r.data.items ?? []);
    const rows: Row[] = data.map((row) => {
      const id = String(row.id);
      let label: string;
      switch (kind) {
        case 'vehicle':
          label = [row.code, row.plate_en, row.brand].filter(Boolean).join(' · ');
          break;
        case 'driver':
          label = [row.code, row.full_name_en ?? row.fullNameEn].filter(Boolean).join(' · ');
          break;
        case 'client':
          label = String(row.name_en ?? row.nameEn ?? row.name ?? row.code ?? id);
          break;
        case 'site':
          label = String(row.name_en ?? row.code ?? id);
          break;
        case 'project':
          label = String(row.name_en ?? row.code ?? id);
          break;
      }
      return { id, label };
    });
    rows.sort((a, b) => a.label.localeCompare(b.label));
    CACHE.set(cacheKey, rows);
    return rows;
  } catch {
    return [];
  }
}

export function EntityPicker({ kind, value, onChange, status = 'active' }: PickerProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchList(kind, status).then((rs) => {
      if (!cancelled) { setRows(rs); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [kind, status]);

  if (loading) {
    return (
      <div className="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-sm text-muted-foreground gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading {kind}s…
      </div>
    );
  }

  return (
    <select
      autoFocus
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    >
      <option value="">— None —</option>
      {rows.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
    </select>
  );
}
